import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { CalendarCheck, X, Check } from "lucide-react";
import { api } from "../../../lib/api";
import { trackEvent, trackReciprocity } from "../../../lib/track";
import { useConsultation } from "../../../context/ConsultationContext";
import { useEscapeKey } from "../../../hooks/useEscapeKey";
import { getAttributionContextLine } from "../../../lib/attribution";
import {
  PRIMARY_CATEGORY_OPTIONS,
  SPECIFIC_INTERESTS,
} from "../../../data/serviceTaxonomy";

const buildInitial = (prefill) => ({
  name: "",
  email: "",
  category: prefill?.category || "Not sure yet",
  specific_interest: prefill?.specific_interest || "Not Sure Yet",
  source_context: prefill?.source_context || "",
  message: "",
});

const ConsultationModal = () => {
  const { isOpen, close, prefill } = useConsultation();
  const [form, setForm] = useState(() => buildInitial(prefill));
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const firstFieldRef = useRef(null);

  // Re-seed form whenever the modal opens with a new prefill.
  useEffect(() => {
    if (isOpen) {
      setForm(buildInitial(prefill));
      setDone(false);
    }
  }, [isOpen, prefill]);

  // Focus first field when modal opens.
  useEffect(() => {
    if (isOpen && firstFieldRef.current) {
      const t = setTimeout(() => firstFieldRef.current?.focus(), 60);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [isOpen]);

  // Body-scroll-lock + signal to the sticky mobile CTA so it can hide.
  useEffect(() => {
    if (!isOpen) return undefined;
    document.documentElement.classList.add("am-modal-open");
    const y = window.scrollY;
    const prev = document.body.style.cssText;
    document.body.style.position = "fixed";
    document.body.style.top = `-${y}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.width = "100%";
    return () => {
      document.documentElement.classList.remove("am-modal-open");
      document.body.style.cssText = prev;
      window.scrollTo(0, y);
    };
  }, [isOpen]);

  const handleClose = useCallback(() => {
    if (busy) return;
    close();
  }, [busy, close]);

  useEscapeKey(handleClose, isOpen && !busy);

  const submit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!form.name || !form.email) {
        toast.error("Name and email please.");
        return;
      }
      try {
        setBusy(true);
        // Append captured src / ref / utm attribution onto the
        // consultation's source_context so the admin Opportunity
        // Signals queue can see the channel the lead came from.
        const attrLine = getAttributionContextLine();
        const composedSource = [form.source_context, attrLine]
          .filter(Boolean)
          .join(" · ");
        await api.post("/consultations", {
          name: form.name,
          email: form.email,
          intent: form.specific_interest,
          category: form.category,
          specific_interest: form.specific_interest,
          source_context: composedSource || undefined,
          message: form.message || undefined,
        });
        trackEvent("consultation_submitted", { metadata: { category: form.category, interest: form.specific_interest } });
        trackReciprocity("consultation_preparation_checklist", { related_user_email: form.email });
        trackEvent("consultation_checklist_viewed");
        setDone(true);
        toast.success(
          "Your request has been sent. Your next aligned step is now in motion."
        );
      } catch {
        toast.error("Try once more. The signal will reach us.");
      } finally {
        setBusy(false);
      }
    },
    [form]
  );

  if (!isOpen) return null;

  return (
    <div
      data-testid="consultation-modal"
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/85 backdrop-blur-md px-4 sm:px-6 py-10"
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="consultation-modal-title"
    >
      <div
        className="glass-card max-w-2xl w-full p-8 sm:p-10 relative max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={handleClose}
          aria-label="Close consultation"
          data-testid="consultation-modal-close"
          className="absolute top-5 right-5 text-themed-muted hover:text-gold transition-colors"
        >
          <X size={18} />
        </button>

        <p className="text-[10px] tracking-[0.4em] uppercase text-gold mb-3">
          Request a Conversation
        </p>
        <h3
          id="consultation-modal-title"
          className="font-serif text-3xl sm:text-4xl text-themed leading-tight"
        >
          {prefill?.title || "Bring your direction. We will help you build it."}
        </h3>
        {prefill?.subtitle && (
          <p className="text-sm text-themed-muted mt-3">{prefill.subtitle}</p>
        )}

        {!done ? (
          <form onSubmit={submit} className="mt-8 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label
                  htmlFor="cm-name"
                  className="text-[10px] tracking-[0.3em] uppercase text-themed-muted"
                >
                  Name
                </label>
                <input
                  ref={firstFieldRef}
                  id="cm-name"
                  data-testid="consultation-modal-name"
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  className="input-bare"
                  required
                />
              </div>
              <div>
                <label
                  htmlFor="cm-email"
                  className="text-[10px] tracking-[0.3em] uppercase text-themed-muted"
                >
                  Email
                </label>
                <input
                  id="cm-email"
                  data-testid="consultation-modal-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  className="input-bare"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label
                  htmlFor="cm-category"
                  className="text-[10px] tracking-[0.3em] uppercase text-themed-muted"
                >
                  Primary path
                </label>
                <select
                  id="cm-category"
                  data-testid="consultation-modal-category"
                  value={form.category}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, category: e.target.value }))
                  }
                  className="input-bare bg-black"
                >
                  {PRIMARY_CATEGORY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value} className="bg-black">
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label
                  htmlFor="cm-interest"
                  className="text-[10px] tracking-[0.3em] uppercase text-themed-muted"
                >
                  Specific interest
                </label>
                <select
                  id="cm-interest"
                  data-testid="consultation-modal-interest"
                  value={form.specific_interest}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, specific_interest: e.target.value }))
                  }
                  className="input-bare bg-black"
                >
                  {SPECIFIC_INTERESTS.map((s) => (
                    <option key={s} value={s} className="bg-black">
                      {s}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {form.source_context && (
              <div className="text-[10px] tracking-[0.22em] uppercase text-gold/70 border border-gold/20 rounded-md px-3 py-2">
                Context: {form.source_context}
              </div>
            )}

            <div>
              <label
                htmlFor="cm-message"
                className="text-[10px] tracking-[0.3em] uppercase text-themed-muted"
              >
                A few sentences on where you are
              </label>
              <textarea
                id="cm-message"
                data-testid="consultation-modal-message"
                rows={4}
                value={form.message}
                onChange={(e) =>
                  setForm((f) => ({ ...f, message: e.target.value }))
                }
                className="input-bare resize-none"
              />
            </div>

            <div className="flex items-center gap-4 pt-2">
              <button
                type="submit"
                disabled={busy}
                data-testid="consultation-modal-submit"
                className="btn-gold disabled:opacity-60"
              >
                {busy ? "Sending…" : "Request a Conversation"}
              </button>
              <button
                type="button"
                data-testid="consultation-modal-cancel"
                onClick={handleClose}
                disabled={busy}
                className="text-xs tracking-[0.2em] uppercase text-themed-muted hover:text-themed disabled:opacity-40"
              >
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <div className="mt-8 py-2" data-testid="consultation-modal-success">
            <div className="text-center">
              <CalendarCheck className="mx-auto text-gold" size={32} />
              <h3 className="font-serif text-3xl text-themed mt-5">
                Received with care.
              </h3>
              <p className="mt-4 text-themed-muted max-w-md mx-auto leading-relaxed">
                Your request has been sent to Authentic Momentum. Your next
                aligned step is now in motion. We will respond within 48 hours.
              </p>
            </div>

            {/* Reciprocity fulfillment: Consultation preparation checklist */}
            <div className="mt-8 glass-card p-6 text-left" data-testid="consultation-checklist">
              <p className="text-[10px] tracking-[0.4em] uppercase text-gold/80 mb-3">
                Your Consultation Preparation Checklist
              </p>
              <ul className="space-y-2 text-sm text-themed-muted">
                {[
                  "What do I most want clarity on right now?",
                  "Where do I feel I am forcing instead of flowing?",
                  "What outcome would make this conversation valuable?",
                  "What opportunity, idea, brand, offer, or life direction feels most alive right now?",
                  "What have I already tried that has not fully worked?",
                  "What support do I actually need next?",
                ].map((line) => (
                  <li key={line} className="flex items-start gap-3">
                    <Check size={13} className="text-gold mt-1 shrink-0" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-5 text-themed-muted text-xs italic">
                Come with honesty over perfection. The goal is not to perform clarity. The goal is to reveal it.
              </p>
            </div>

            <div className="text-center">
              <button
                type="button"
                onClick={handleClose}
                className="btn-ghost-gold mt-8"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConsultationModal;
